import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ClientCurrentStageDto } from '../../services/clientsService';

/* תא סטטוס עם אינדיקטור וטולטיפ כשיש יותר מסטטוס Current אחד */
/* האינדיקטור מוצג רק כאשר יש 2 סטטוסים או יותר */
/* סדר הסטטוסים בטולטיפ מגיע מ-sortOrder (שמוגדר בטבלת Status) — לא מקודד בקומפוננט */

interface Props {
  currentStatusName?: string;
  currentStatusColor?: string;
  currentStages?: ClientCurrentStageDto[];
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const StatusText = styled.span<{ color: string }>`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.color};
  filter: saturate(0.7) brightness(0.85);
`;

const Indicator = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 18px;
  padding: 0 6px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  color: var(--text-secondary, #555);
  background: var(--hover-background, #eef0f3);
  border: 1px solid var(--border-color, #d6d9de);
  border-radius: 10px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover,
  &:focus-visible {
    background: var(--surface-hover, #e3e7ec);
    border-color: var(--border-strong, #b9c0c9);
    outline: none;
  }
`;

const Popover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 50%;
  transform: translateX(50%);
  z-index: 50;
  min-width: 220px;
  max-width: 280px;
  background: #ffffff;
  border: 1px solid var(--border-color, #d6d9de);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 24, 39, 0.12);
  padding: 8px 10px;
  text-align: right;
  direction: rtl;
`;

const PopoverHeader = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary, #6b7280);
  padding: 2px 4px 6px;
  border-bottom: 1px solid var(--border-color, #eef0f3);
  margin-bottom: 6px;
`;

const StageRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 4px;

  & + & {
    border-top: 1px dashed var(--border-color, #eef0f3);
  }
`;

const StageLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const StageDot = styled.span<{ color?: string }>`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.color || 'var(--text-secondary, #6b7280)'};
`;

const StageName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary, #1f2937);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StageDate = styled.span`
  font-size: 11px;
  color: var(--text-secondary, #6b7280);
  white-space: nowrap;
`;

const formatStageDate = (iso?: string): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const StatusCellWithTooltip: React.FC<Props> = ({
  currentStatusName,
  currentStatusColor,
  currentStages,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  /* סגירה בלחיצה מחוץ לטולטיפ — תומך גם ב-touch על מובייל */
  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (event: MouseEvent | TouchEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [isOpen]);

  if (!currentStatusName || !currentStatusColor) {
    return <>-</>;
  }

  const stages = currentStages ?? [];
  const hasMultiple = stages.length > 1;
  const additionalCount = hasMultiple ? stages.length - 1 : 0;

  return (
    <Wrapper
      ref={wrapperRef}
      onMouseEnter={hasMultiple ? () => setIsOpen(true) : undefined}
      onMouseLeave={hasMultiple ? () => setIsOpen(false) : undefined}
    >
      <StatusText color={currentStatusColor}>{currentStatusName}</StatusText>
      {hasMultiple && (
        <>
          <Indicator
            type="button"
            aria-label={`${stages.length} סטטוסים פעילים`}
            aria-haspopup="true"
            aria-expanded={isOpen}
            onClick={e => {
              e.stopPropagation();
              setIsOpen(prev => !prev);
            }}
          >
            +{additionalCount}
          </Indicator>
          {isOpen && (
            <Popover role="tooltip" onClick={e => e.stopPropagation()}>
              <PopoverHeader>סטטוסים פעילים ({stages.length})</PopoverHeader>
              {stages.map(stage => (
                <StageRow key={stage.statusId}>
                  <StageLabel>
                    <StageDot color={stage.statusColor} />
                    <StageName title={stage.statusName}>{stage.statusName}</StageName>
                  </StageLabel>
                  {stage.stageDate && <StageDate>{formatStageDate(stage.stageDate)}</StageDate>}
                </StageRow>
              ))}
            </Popover>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default StatusCellWithTooltip;
